import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CoachLayout from "@/components/CoachLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { useFeatures } from "@/lib/features";
import { ProvenanceBadge } from "@/components/Badge";
import { ArrowLeft, Loader2, FileText, Sparkles, Check, Clock, X, Dumbbell, Plus, Pencil, Trash2, Gauge, Trophy, Target, RotateCcw, LineChart } from "lucide-react";
import { toast } from "sonner";
import ProgramAssistant from "@/components/ProgramAssistant";

export default function CoachClient() {
  const { id } = useParams();
  const navigate = useNavigate();
  const features = useFeatures();
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === "admin";
  const [detail, setDetail] = useState(null);
  const [synth, setSynth] = useState(null);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [note, setNote] = useState("");
  const [parsing, setParsing] = useState(false);
  const [proposed, setProposed] = useState(null);
  const [pending, setPending] = useState([]);
  const [modeEdit, setModeEdit] = useState(false);
  const [modeForm, setModeForm] = useState({ choice: "progresser", race_distance: "10km", race_date: "" });
  const [savingMode, setSavingMode] = useState(false);

  const loadPending = () => api.get(`/coach/clients/${id}/pending-adaptations`).then((r) => setPending(r.data.pending || [])).catch(() => {});

  const [recap, setRecap] = useState(null);
  useEffect(() => {
    api.get(`/coach/clients/${id}`).then((r) => setDetail(r.data));
    api.get(`/coach/clients/${id}/synthesis`).then((r) => setSynth(r.data)).catch(() => {});
    api.get(`/coach/clients/${id}/week-recap`).then((r) => setRecap(r.data)).catch(() => {});
    loadPending();
  }, [id]);

  const decideAdaptation = async (adaptationId, action) => {
    try {
      await api.post(`/coach/adaptation/${adaptationId}/${action}`);
      toast.success(action === "approve" ? "Adaptation validée et appliquée" : "Adaptation refusée");
      loadPending();
      api.get(`/coach/clients/${id}`).then((r) => setDetail(r.data));
    } catch { toast.error("Erreur"); }
  };

  const reloadDetail = () => api.get(`/coach/clients/${id}`).then((r) => setDetail(r.data));

  const saveMode = async () => {
    if (modeForm.choice === "course" && !modeForm.race_date) return toast.error("Date de course requise");
    setSavingMode(true);
    try {
      await api.patch(`/coach/clients/${id}/coaching-mode`, modeForm);
      toast.success("Mode de coaching mis à jour · plan régénéré");
      setModeEdit(false);
      await reloadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSavingMode(false); }
  };

  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});

  const startEdit = (s) => { setEditId(s.session_id); setForm({ day: s.day || "", title: s.title || "", focus: s.focus || "", duration_min: s.duration_min || "" }); };
  const saveSession = async () => {
    try {
      await api.patch(`/coach/clients/${id}/sessions/${editId}`, { ...form, duration_min: form.duration_min ? Number(form.duration_min) : null });
      toast.success("Séance modifiée"); setEditId(null); reloadDetail();
    } catch { toast.error("Erreur"); }
  };
  const deleteSession = async (sid) => {
    try { await api.delete(`/coach/clients/${id}/sessions/${sid}`); toast.success("Séance supprimée"); reloadDetail(); }
    catch { toast.error("Erreur"); }
  };
  const addSession = async () => {
    try { await api.post(`/coach/clients/${id}/sessions`, { day: "À planifier", title: "Nouvelle séance", focus: "course", duration_min: 45 }); toast.success("Séance ajoutée"); reloadDetail(); }
    catch { toast.error("Erreur"); }
  };

  const [regenerating, setRegenerating] = useState(false);
  const regenerateProgram = async () => {
    const ok = window.confirm(
      "Supprimer le programme EN COURS de ce coureur et le régénérer par l'IA ?\n\n" +
      "Les séances déjà réalisées (historique/évolution) sont conservées ; seules les séances à venir seront remplacées par un nouveau plan basé sur son profil actuel.\n\nCette action est irréversible."
    );
    if (!ok) return;
    setRegenerating(true);
    try {
      await api.post(`/coach/clients/${id}/regenerate-program`);
      toast.success("Programme supprimé et régénéré par l'IA");
      await reloadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de la régénération"); }
    finally { setRegenerating(false); }
  };

  const [vmaInput, setVmaInput] = useState("");
  const [vmaPaces, setVmaPaces] = useState(null);
  const saveVma = async () => {
    const v = parseFloat(vmaInput);
    if (!v || v <= 0) { toast.error("VMA invalide"); return; }
    try { const r = await api.post(`/coach/clients/${id}/vma`, { vma: v }); setVmaPaces(r.data.paces); toast.success("VMA mise à jour · allures recalculées"); reloadDetail(); }
    catch { toast.error("Erreur"); }
  };

  const loadReport = async () => {
    setLoadingReport(true);
    try { const r = await api.get(`/coach/clients/${id}/report`); setReport(r.data); }
    catch { toast.error("Erreur"); } finally { setLoadingReport(false); }
  };

  const parse = async () => {
    if (!note.trim()) return;
    setParsing(true);
    try { const r = await api.post("/coach/decision/parse", { client_id: id, note }); setProposed(r.data); }
    catch { toast.error("Erreur"); } finally { setParsing(false); }
  };

  const validate = async () => {
    const dir = {
      frequency: proposed.frequency, max_duration: proposed.max_duration,
      objective: proposed.objective, duration_directive: proposed.duration_directive, notes: proposed.notes,
    };
    await api.post("/coach/decision/validate", { client_id: id, directives: dir });
    toast.success("Directives validées — prioritaires pour le moteur");
    setProposed(null); setNote("");
    api.get(`/coach/clients/${id}`).then((r) => setDetail(r.data));
  };

  const cancelAccompaniment = async () => {
    if (!window.confirm("Annuler l'accompagnement Premium de ce client ? Une place se libère et le premier coureur en liste d'attente sera notifié par email.")) return;
    try {
      const r = await api.post(`/coach/clients/${id}/cancel-accompaniment`);
      toast.success(r.data.notified > 0 ? "Accompagnement annulé · liste d'attente notifiée par email" : "Accompagnement annulé · place libérée");
      reloadDetail();
    } catch { toast.error("Erreur lors de l'annulation"); }
  };

  if (!detail) return <CoachLayout><div className="pulse-organic w-3 h-3 rounded-full bg-obsidian mx-auto mt-20" /></CoachLayout>;
  const prog = detail.progression;
  const u = detail.user;

  return (
    <CoachLayout>
      <button onClick={() => navigate("/coach")} className="inline-flex items-center gap-1.5 text-sm text-obsidian/50 hover:text-obsidian"><ArrowLeft size={15} /> Tous les clients</button>

      <div className="fade-up mt-4 flex items-center gap-4">
        {u.picture ? <img src={u.picture} alt="" className="w-14 h-14 rounded-full object-cover bg-secondary" />
          : <span className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center text-lg font-medium">{(u.name || "?").charAt(0)}</span>}
        <div>
          <h1 className="text-2xl font-medium">{u.name}</h1>
          <div className="text-sm text-obsidian/50">{detail.profile?.objectives?.principal || "—"}</div>
        </div>
        {(u.plan === "premium" && (u.accompaniment_active ?? detail.coaching)) && (
          <button data-testid="cancel-accompaniment" onClick={cancelAccompaniment}
            className="ml-auto rounded-full border border-red-200 text-red-600 px-4 py-2 text-sm hover:bg-red-50 transition-colors">
            Annuler l'accompagnement
          </button>
        )}
      </div>

      {/* MODE DE COACHING */}
      {detail.coaching && (
        <div className="mt-6 bg-white border border-border p-5 sm:p-6 fade-up" data-testid="coach-mode-block">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase font-semibold text-obsidian/50">
                {detail.coaching.coaching_mode === "event" ? <Trophy size={14} className="text-sage" /> : <Target size={14} className="text-sage" />}
                Mode de coaching
              </div>
              {detail.coaching.coaching_mode === "event" ? (
                <div className="mt-2">
                  <div className="text-base font-medium" data-testid="coach-mode-value">Préparation d'épreuve — {detail.coaching.race?.race_name}{detail.coaching.race?.distance ? ` (${detail.coaching.race.distance})` : ""}</div>
                  <div className="mt-1 text-sm text-obsidian/60">
                    {detail.coaching.race?.race_date} · {detail.coaching.weeks_left} sem. restantes · Phase : {detail.coaching.phase_label || "—"}
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <div className="text-base font-medium" data-testid="coach-mode-value">Coaching continu — {detail.coaching.objective_label}</div>
                  <div className="mt-1 text-sm text-obsidian/60">Cycle {detail.coaching.cycle_number}{detail.coaching.phase_label ? ` · ${detail.coaching.phase_label}` : ""}</div>
                </div>
              )}
            </div>
            <button data-testid="coach-mode-edit" onClick={() => setModeEdit(!modeEdit)} className="rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-obsidian/5">Changer</button>
          </div>

          {modeEdit && (
            <div className="mt-4 border-t border-border pt-4 space-y-3" data-testid="coach-mode-editor">
              <div className="flex flex-wrap gap-2">
                {[["reprendre", "Reprise"], ["progresser", "Progression"], ["course", "Préparation course"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setModeForm({ ...modeForm, choice: k })}
                    className={`rounded-full px-4 py-2 text-sm border ${modeForm.choice === k ? "border-obsidian bg-obsidian text-white" : "border-border"}`}>{lbl}</button>
                ))}
              </div>
              {modeForm.choice === "course" && (
                <div className="grid grid-cols-2 gap-2.5">
                  <select value={modeForm.race_distance} onChange={(e) => setModeForm({ ...modeForm, race_distance: e.target.value })}
                    className="rounded-lg border border-border px-3 py-2 text-sm bg-white">
                    <option value="5km">5 km</option><option value="10km">10 km</option><option value="semi">Semi</option><option value="marathon">Marathon</option>{features.trail_enabled && <option value="trail">Trail</option>}
                  </select>
                  <input type="date" value={modeForm.race_date} onChange={(e) => setModeForm({ ...modeForm, race_date: e.target.value })}
                    className="rounded-lg border border-border px-3 py-2 text-sm" />
                </div>
              )}
              <button data-testid="coach-mode-save" onClick={saveMode} disabled={savingMode}
                className="rounded-full bg-obsidian text-white px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
                {savingMode ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Appliquer et régénérer
              </button>
            </div>
          )}
        </div>
      )}

      {/* ADAPTATIONS IA EN ATTENTE DE VALIDATION */}
      {pending.length > 0 && (
        <div data-testid="pending-adaptations" className="mt-6 bg-amber-50 border border-amber-200 p-6 fade-up">
          <div className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase font-semibold text-amber-700">
            <Clock size={14} /> Adaptations IA en attente de ta validation ({pending.length})
          </div>
          <p className="mt-1 text-sm text-amber-800/70">Modifications majeures proposées par l'IA au coureur. Elles ne sont appliquées qu'après ta validation.</p>
          <div className="mt-4 space-y-3">
            {pending.map((a) => (
              <div key={a.adaptation_id} data-testid={`pending-adaptation-${a.adaptation_id}`} className="bg-white border border-amber-200 rounded-sm p-4">
                <div className="text-sm font-medium text-obsidian/90">{a.summary}</div>
                {a.reason && <div className="mt-0.5 text-xs text-obsidian/50">Motif du coureur : {a.reason}</div>}
                <div className="mt-1 text-[11px] text-obsidian/40">{a.created_at && new Date(a.created_at).toLocaleString("fr-FR")}</div>
                <div className="mt-3 flex gap-2">
                  <button data-testid={`approve-adaptation-${a.adaptation_id}`} onClick={() => decideAdaptation(a.adaptation_id, "approve")}
                    className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm inline-flex items-center gap-1.5"><Check size={14} /> Valider et appliquer</button>
                  <button data-testid={`reject-adaptation-${a.adaptation_id}`} onClick={() => decideAdaptation(a.adaptation_id, "reject")}
                    className="rounded-sm border border-border px-4 py-2 text-sm inline-flex items-center gap-1.5 hover:bg-obsidian/5"><X size={14} /> Refuser</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SYNTHÈSE IA — affichée en premier */}
      <div className="mt-8 bg-white border border-border p-6">
        <ProvenanceBadge type="analyse">SYNTHÈSE IA</ProvenanceBadge>
        {synth ? (
          <>
            <p className="mt-4 text-obsidian/85 leading-relaxed">{synth.synthesis}</p>
            {synth.recommendation && (
              <div className="mt-4 border-l-2 border-sage pl-3">
                <ProvenanceBadge type="recommandation" className="mb-2" />
                <p className="text-sm text-obsidian/70">{synth.recommendation}</p>
              </div>
            )}
          </>
        ) : <div className="mt-4 flex items-center gap-2 text-sm text-obsidian/40"><Loader2 size={15} className="animate-spin" /> Génération de la synthèse…</div>}
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* COMPTE RENDU AVANT RDV */}
          <div className="bg-white border border-border p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium flex items-center gap-2"><FileText size={18} strokeWidth={1.6} /> Préparer mon rendez-vous</h2>
              {!report && <button data-testid="prepare-rdv-button" onClick={loadReport} disabled={loadingReport}
                className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60">
                {loadingReport ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Générer</button>}
            </div>
            {report && (
              <div className="mt-5 space-y-4 fade-up">
                <ReportRow label="Régularité" value={report.regularite} />
                <ReportRow label="Performances" value={report.performances} />
                <ReportRow label="Corps" value={report.corps} />
                <ReportRow label="Récupération" value={report.recuperation} />
                <ReportList label="Points positifs" items={report.points_positifs} />
                <ReportList label="Points à discuter" items={report.points_a_discuter} />
                <ReportList label="Questions suggérées" items={report.questions_suggerees} />
              </div>
            )}
          </div>

          {/* DÉCISIONS APRÈS RDV */}
          <div className="bg-white border border-border p-6">
            <h2 className="text-lg font-medium">Après le rendez-vous</h2>
            <p className="mt-1 text-sm text-obsidian/50">Dicte ou écris naturellement. L'IA transforme en directives structurées.</p>
            <textarea data-testid="decision-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="Ex : Thomas va bien. On passe à 3 séances/semaine pendant un mois, max 45 min. Objectif maintenu."
              className="mt-4 w-full resize-none border border-border p-3 text-sm rounded-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
            <button data-testid="decision-parse-button" onClick={parse} disabled={parsing || !note.trim()}
              className="mt-3 rounded-sm border border-obsidian px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-obsidian hover:text-white transition-[background-color,color] disabled:opacity-40">
              {parsing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Structurer
            </button>

            {proposed && (
              <div className="mt-5 border border-border p-4 fade-up">
                <ProvenanceBadge type="recommandation" className="mb-3">DÉCISIONS PROPOSÉES</ProvenanceBadge>
                <div className="space-y-1.5 text-sm">
                  <PropRow k="Fréquence" v={proposed.frequency} />
                  <PropRow k="Durée maximale" v={proposed.max_duration} />
                  <PropRow k="Objectif" v={proposed.objective} />
                  <PropRow k="Durée de la directive" v={proposed.duration_directive} />
                  <PropRow k="Notes" v={proposed.notes} />
                </div>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => setProposed(null)} className="rounded-sm border border-border px-4 py-2 text-sm">Modifier</button>
                  <button data-testid="decision-validate-button" onClick={validate} className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm inline-flex items-center gap-1.5"><Check size={14} /> Valider</button>
                </div>
              </div>
            )}
          </div>

          {/* VMA DU COUREUR (coach) */}
          <div className="bg-white border border-border p-6" data-testid="coach-vma-editor">
            <div className="flex items-center gap-2 text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50"><Gauge size={15} /> VMA du coureur</div>
            <p className="mt-1 text-sm text-obsidian/50">Modifier la VMA recalcule automatiquement toutes les allures et la programmation.</p>
            <div className="mt-3 flex items-end gap-2">
              <div>
                <label className="block text-xs text-obsidian/50 mb-1">VMA (km/h)</label>
                <input data-testid="coach-vma-input" type="number" step="0.1"
                  placeholder={detail.profile?.training?.vma || "—"} value={vmaInput} onChange={(e) => setVmaInput(e.target.value)}
                  className="w-32 border border-border rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
              </div>
              <button data-testid="coach-vma-save" onClick={saveVma} className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm inline-flex items-center gap-1.5"><Check size={13} /> Enregistrer</button>
              {detail.profile?.training?.vma && <span className="text-sm text-obsidian/50 pb-2">Actuelle : {detail.profile.training.vma} km/h</span>}
            </div>
            {vmaPaces && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {vmaPaces.map((p) => (
                  <div key={p.zone} className="rounded-lg border border-border px-3 py-2">
                    <div className="text-[11px] text-obsidian/50 truncate">{p.zone}</div>
                    <div className="text-sm font-medium">{p.pace}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RÉCAP HEBDO (RPE prévu/réel, douleurs, décision MAI) */}
          {recap && recap.weeks && recap.weeks.length > 0 && (
            <div className="bg-white border border-border p-6 mb-6" data-testid="coach-week-recap">
              <h2 className="text-lg font-medium flex items-center gap-2"><LineChart size={18} strokeWidth={1.6} /> Récap par semaine</h2>
              <p className="mt-1 text-sm text-obsidian/50">RPE prévu vs réel, douleurs déclarées, décision MAI et adaptations.</p>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-obsidian/45 border-b border-border">
                    <th className="text-left font-medium py-2 pr-3">Semaine</th>
                    <th className="text-left font-medium py-2 px-3">Réalisé</th>
                    <th className="text-left font-medium py-2 px-3">RPE prévu/réel</th>
                    <th className="text-left font-medium py-2 px-3">Douleur</th>
                    <th className="text-left font-medium py-2 px-3">Décision MAI</th>
                  </tr></thead>
                  <tbody>
                    {recap.weeks.map((w, i) => (
                      <tr key={i} className="border-b border-border/50 last:border-0">
                        <td className="py-2 pr-3 whitespace-nowrap">S{w.week}{w.adapted > 0 ? " ·adapté" : ""}</td>
                        <td className="py-2 px-3 tabnum">{w.completed}/{w.planned}</td>
                        <td className="py-2 px-3 tabnum">{w.rpe_planned ?? "—"} / <span className={w.rpe_actual != null && w.rpe_planned != null && w.rpe_actual - w.rpe_planned >= 2 ? "text-destructive font-semibold" : ""}>{w.rpe_actual ?? "—"}</span></td>
                        <td className="py-2 px-3">{w.max_pain === 2 ? <span className="text-destructive">importante</span> : w.max_pain === 1 ? "légère" : "—"}</td>
                        <td className="py-2 px-3">{w.decision ? <span className={`text-xs font-semibold ${w.decision === "ALERTE_COACH" ? "text-destructive" : w.decision === "RÉDUIRE" ? "text-amber-600" : "text-sage"}`}>{w.decision}</span> : <span className="text-obsidian/30">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ÉDITION DU PROGRAMME PAR LE COACH */}
          {/* Brouillon coach V1 masqué pendant le lancement (aucune publication V1 au client). */}
          {false && <ProgramAssistant clientId={id} onPublished={reloadDetail} />}

          <div className="bg-white border border-border p-6" data-testid="coach-program-editor">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium flex items-center gap-2"><Dumbbell size={18} strokeWidth={1.6} /> Programme du coureur</h2>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <button data-testid="coach-regenerate-program" onClick={regenerateProgram} disabled={regenerating}
                    className="rounded-sm border border-destructive/40 text-destructive px-3 py-1.5 text-sm inline-flex items-center gap-1.5 hover:bg-destructive hover:text-white transition-colors disabled:opacity-50">
                    {regenerating ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                    {regenerating ? "Régénération…" : "Supprimer & régénérer"}
                  </button>
                )}
                <button data-testid="coach-add-session" onClick={addSession}
                  className="rounded-sm border border-obsidian px-3 py-1.5 text-sm inline-flex items-center gap-1.5 hover:bg-obsidian hover:text-white transition-colors"><Plus size={14} /> Séance</button>
              </div>
            </div>
            <p className="mt-1 text-sm text-obsidian/50">Consulte et modifie chaque séance directement.</p>
            <div className="mt-4 space-y-2">
              {(!detail.sessions || detail.sessions.length === 0) && <p className="text-sm text-obsidian/40">Aucune séance dans le programme.</p>}
              {(detail.sessions || []).map((s) => (
                <div key={s.session_id} data-testid={`coach-session-${s.session_id}`} className="border border-border rounded-sm p-3">
                  {editId === s.session_id ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <input value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} placeholder="Jour" className="border border-border rounded-sm px-2 py-1.5 text-sm" data-testid="edit-day" />
                        <input value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} placeholder="Durée (min)" type="number" className="border border-border rounded-sm px-2 py-1.5 text-sm" data-testid="edit-duration" />
                      </div>
                      <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" className="w-full border border-border rounded-sm px-2 py-1.5 text-sm" data-testid="edit-title" />
                      <input value={form.focus} onChange={(e) => setForm({ ...form, focus: e.target.value })} placeholder="Focus" className="w-full border border-border rounded-sm px-2 py-1.5 text-sm" data-testid="edit-focus" />
                      <div className="flex gap-2">
                        <button data-testid="save-session" onClick={saveSession} className="rounded-sm bg-obsidian text-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5"><Check size={13} /> Enregistrer</button>
                        <button onClick={() => setEditId(null)} className="rounded-sm border border-border px-3 py-1.5 text-sm">Annuler</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{s.title}</div>
                        <div className="text-xs text-obsidian/50">{s.day} · {s.duration_min} min · {s.focus}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button data-testid={`edit-session-${s.session_id}`} onClick={() => startEdit(s)} className="p-1.5 rounded-sm text-obsidian/50 hover:text-obsidian hover:bg-obsidian/5"><Pencil size={15} /></button>
                        <button data-testid={`delete-session-${s.session_id}`} onClick={() => deleteSession(s.session_id)} className="p-1.5 rounded-sm text-obsidian/40 hover:text-destructive hover:bg-destructive/10"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* DÉTAILS */}
        <div className="space-y-6">
          <div className="bg-white border border-border p-6">
            <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50 mb-3">Données détaillées</div>
            <Detail k="Régularité" v={`${Math.round(prog.regularity.rate * 100)}%`} />
            <Detail k="Réalisées" v={prog.regularity.done} />
            <Detail k="Partielles" v={prog.regularity.partial} />
            <Detail k="Manquées" v={prog.regularity.missed} />
            <Detail k="Niveau" v={detail.profile?.training?.niveau} />
          </div>

          {detail.directives && (
            <div className="bg-white border border-border p-6">
              <ProvenanceBadge type="coach" className="mb-3">DIRECTIVES ACTIVES</ProvenanceBadge>
              <div className="space-y-1.5 text-sm">
                <PropRow k="Fréquence" v={detail.directives.directives?.frequency} />
                <PropRow k="Durée max" v={detail.directives.directives?.max_duration} />
                <PropRow k="Objectif" v={detail.directives.directives?.objective} />
              </div>
            </div>
          )}
        </div>
      </div>
    </CoachLayout>
  );
}

function ReportRow({ label, value }) {
  if (!value) return null;
  return (<div><div className="text-xs tracking-[0.14em] uppercase font-semibold text-obsidian/50">{label}</div><p className="mt-1 text-sm text-obsidian/80">{value}</p></div>);
}
function ReportList({ label, items }) {
  if (!items || items.length === 0) return null;
  return (<div><div className="text-xs tracking-[0.14em] uppercase font-semibold text-obsidian/50">{label}</div><ul className="mt-1 space-y-1">{items.map((it, i) => <li key={i} className="text-sm text-obsidian/80 flex gap-2"><span className="text-sage">—</span>{it}</li>)}</ul></div>);
}
function PropRow({ k, v }) {
  if (v === null || v === undefined || v === "") return null;
  return (<div className="flex justify-between gap-4"><span className="text-obsidian/50">{k}</span><span className="font-medium text-right">{String(v)}</span></div>);
}
function Detail({ k, v }) {
  if (v === null || v === undefined || v === "") return null;
  return (<div className="flex justify-between py-1.5 border-b border-border last:border-0 text-sm"><span className="text-obsidian/50">{k}</span><span className="font-medium tabnum">{String(v)}</span></div>);
}
